import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { GridContainer, GridItem } from '@inter-ikea-guida/framework';
import { useIsMetricMeasures } from '../../hooks/useIsMetricMeasures';
import { getCMSState, getFormState } from '../../store/app/selectors';
import { getTranslation } from '../../store/i18n/selectors';
import { SELECT, SINGLE_WALL, SIZE_WIDTH_LEFT_85, SIZE_WIDTH_LEFT_88, SIZE_WIDTH_RIGHT_85, SIZE_WIDTH_RIGHT_88, TOGGLE } from '../../utils';
import { getSingleSurveyPayload } from '../../utils/insights/getSurveyPayload';
import { sendInsightsEvent } from '../../utils/insights/sendInsights';
import { getWidths, handleInputCombinations, useUpdatedWallValues } from '../../views/viewUtils';
import { InlineMessage } from '../quiz/QuizInlineMessage';
import { CornerWallFormSelect } from './CornerWallFormSelect';
import { FilterToggle } from './FilterToggle';
import { StyledForm, StyledSelectContainer } from './styles';
/**
 * Generate data for inputs depending on the id.
 *
 * @param id Select form id.
 * @param depthRight
 * @param depthLeft
 * @returns Array with data objects for all selects.
 */
const getSelectData = (id, depthRight, depthLeft) => {
    return [
        {
            key: `select-${id}-right-1`,
            orientation: 'left',
            show: id === SIZE_WIDTH_LEFT_85 && depthRight === 1
        },
        {
            key: `select-${id}-right-0`,
            orientation: 'left',
            show: id === SIZE_WIDTH_LEFT_88 && depthRight === 0
        },
        {
            key: `select-${id}-left-1`,
            orientation: 'right',
            show: id === SIZE_WIDTH_RIGHT_85 && depthLeft === 1
        },
        {
            key: `select-${id}-left-0`,
            orientation: 'right',
            show: id === SIZE_WIDTH_RIGHT_88 && depthLeft === 0
        }
    ];
};
export const CornerWallForm = ({ selectOption, shouldAnimate }) => {
    const translate = useSelector(getTranslation);
    const formState = useSelector(getFormState);
    const cmsState = useSelector(getCMSState);
    const filterTypes = [TOGGLE, SELECT];
    const useMetricMeasures = useIsMetricMeasures();
    const { leftWidth, rightWidth } = getWidths(formState);
    const { wallOneValues, wallTwoValues } = useUpdatedWallValues(formState.size_depth_left, formState.size_depth_right, leftWidth, rightWidth);
    /**
     * Handle the relationship between the two width inputs in the corner configuration.
     * Some selections prevent certain values in the other input and vice versa.
     */
    useEffect(() => {
        handleInputCombinations(formState, cmsState, wallOneValues, wallTwoValues, selectOption);
    }, [cmsState, formState, selectOption, wallOneValues, wallTwoValues]);
    const onSelect = (id, value, type, selectedIndex) => {
        selectOption(id, value, type, selectedIndex);
        sendInsightsEvent('whatIsMe', 'select_survey_option', getSingleSurveyPayload(id, value));
    };
    return (_jsx(StyledForm, { "$shouldAnimate": shouldAnimate, children: _jsx(GridContainer, { columns: 4, children: cmsState.form
                .filter(item => filterTypes.includes(item.type))
                .filter(item => item.valid !== SINGLE_WALL)
                .map((item, index) => {
                const { type, label, options, id } = item;
                switch (type) {
                    case TOGGLE:
                        return (_jsxs(GridItem, { columnSpan: { s: 4 }, children: [_jsx(FilterToggle, { label: translate(label), dataTestId: `${type}-${id}`, options: options, useMetricMeasures: useMetricMeasures, formState: formState, formObjectId: id, onClick: onSelect }, index), id === 'size_height' && _jsx(InlineMessage, { hasMarginBottom: true })] }, index));
                    case SELECT: {
                        const { id, options } = item;
                        const depthLeft = formState.selectState?.size_depth_left?.selectedIndex;
                        const depthRight = formState.selectState?.size_depth_right?.selectedIndex;
                        // Create select data for all possible select inputs.
                        const selects = getSelectData(id, depthRight, depthLeft);
                        const inputOrientation = item.id.includes('left') ? 'left' : 'right';
                        // Each "input slot" in the form can be occupied by one input at any given time, either th 85cm variant or the 88cm variant.
                        // However, for some reason two inputs are always present in the dom for the same variant, e.g 2 x 85cm inputs.
                        // This is really strange, but somehow prevents flickering when switching depth of a section.
                        // TODO: Can we clean this up and only render the correct input?
                        return (_jsx(GridItem, { columnSpan: { s: 4 }, children: selects
                                .filter(s => s.orientation === inputOrientation)
                                .map((select, index) => (_jsx(StyledSelectContainer, { "$show": select.show, "data-testid": select.key, children: _jsx(CornerWallFormSelect, { id: id, labelText: translate(label), dataTestId: id, value: formState[id], options: options, onValueChange: onSelect, index: index }) }, select.key))) }, `select-container-${index}`));
                    }
                    default:
                        return _jsx(_Fragment, {});
                }
            }) }) }));
};
//# sourceMappingURL=CornerWallForm.js.map